(() => {
  'use strict';

  const ROOT_SELECTOR = '.boost-sd__product-filter-fallback, .boost-sd__filter-product-list';
  const SUMMARY_ID = 'b2-active-filter-summary';
  const MOBILE_QUERY = '(max-width: 798px)';
  const IS_COLLECTION_PAGE = window.location.pathname.startsWith('/collections/');
  const TECHNICAL_KEYS = new Set([
    'sort_by','sort','page','view','q','type','constraint',
    'preview_theme_id','section_id','variant','currency','country',
    '_pos','_sid','_ss','_su_rec','_su_rec_id','gclid','fbclid'
  ]);

  let timer = 0;

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

  const isTechnicalKey = (key) => {
    if (!key) return true;
    const lower = key.toLowerCase();
    return TECHNICAL_KEYS.has(lower) || lower.startsWith('utm_');
  };

  const splitEntry = (raw) => {
    if (raw.includes('||')) return { separator: '||', values: raw.split('||').filter(Boolean) };
    if (raw.includes(',')) return { separator: ',', values: raw.split(',').filter(Boolean) };
    return { separator: null, values: [raw] };
  };

  const decode = (raw) => {
    let value = String(raw ?? '').replace(/\+/g, ' ').trim();
    try { value = decodeURIComponent(value); } catch (_) {}
    return value.replace(/_/g, ' ').trim();
  };

  const collectFilters = () => {
    const url = new URL(window.location.href);
    const filters = [];

    for (const [key, rawEntry] of url.searchParams.entries()) {
      if (isTechnicalKey(key)) continue;

      const { values } = splitEntry(rawEntry);
      values.forEach((rawValue) => {
        const label = decode(rawValue);
        if (!label) return;
        filters.push({ key, rawEntry, rawValue, label });
      });
    }

    return filters;
  };

  const updateParamAfterRemoval = (url, item) => {
    const entries = url.searchParams.getAll(item.key);
    url.searchParams.delete(item.key);
    let removed = false;

    entries.forEach((entry) => {
      const parsed = splitEntry(entry);

      if (parsed.separator) {
        const next = [...parsed.values];
        const index = removed ? -1 : next.indexOf(item.rawValue);
        if (index >= 0) {
          next.splice(index, 1);
          removed = true;
        }
        if (next.length) url.searchParams.append(item.key, next.join(parsed.separator));
        return;
      }

      if (!removed && entry === item.rawEntry) {
        removed = true;
        return;
      }

      url.searchParams.append(item.key, entry);
    });
  };

  const removeFilter = (item) => {
    const url = new URL(window.location.href);
    updateParamAfterRemoval(url, item);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };

  const clearFilters = () => {
    const url = new URL(window.location.href);
    Array.from(new Set(Array.from(url.searchParams.keys()))).forEach((key) => {
      if (!isTechnicalKey(key)) url.searchParams.delete(key);
    });
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  };

  const buildCollectionDestination = (destination) => {
    const sourceUrl = new URL(window.location.href);
    const targetUrl = new URL(destination, window.location.origin);

    for (const [key, value] of sourceUrl.searchParams.entries()) {
      if (isTechnicalKey(key)) continue;
      targetUrl.searchParams.append(key, value);
    }

    const previewThemeId = sourceUrl.searchParams.get('preview_theme_id');
    if (previewThemeId) targetUrl.searchParams.set('preview_theme_id', previewThemeId);

    return targetUrl;
  };

  const findCountRow = () => {
    const count = document.querySelector('.boost-sd__product-count');
    if (!count) return null;

    let row = count.parentElement || count;
    for (let i = 0; i < 3 && row.parentElement; i += 1) {
      const parent = row.parentElement;
      const rect = parent.getBoundingClientRect();
      if (rect.width >= window.innerWidth * 0.72 && rect.height > 30 && rect.height < 180) {
        row = parent;
      } else {
        break;
      }
    }
    return row;
  };

  const renderFilterSummary = () => {
    const existing = document.getElementById(SUMMARY_ID);

    if (!IS_COLLECTION_PAGE) {
      existing?.remove();
      return;
    }

    if (!isMobile()) {
      existing?.remove();
      return;
    }

    const filters = collectFilters();
    if (!filters.length) {
      existing?.remove();
      return;
    }

    const anchor = findCountRow();
    if (!anchor || !anchor.isConnected) return;

    const signature = filters.map((item) => item.key + '=' + item.rawValue).join('|');
    if (existing?.dataset.signature === signature) return;

    const summary = document.createElement('section');
    summary.id = SUMMARY_ID;
    summary.className = 'b2-active-filter-summary';
    summary.dataset.signature = signature;
    summary.setAttribute('aria-label', 'Filtri attivi');

    const label = document.createElement('span');
    label.className = 'b2-active-filter-summary__label';
    label.textContent = 'Filtri:';
    summary.appendChild(label);

    const chipList = document.createElement('div');
    chipList.className = 'b2-active-filter-summary__chips';

    filters.forEach((item) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'b2-active-filter-chip';
      button.textContent = item.label + ' ×';
      button.setAttribute('aria-label', 'Rimuovi filtro ' + item.label);
      button.addEventListener('click', () => removeFilter(item));
      chipList.appendChild(button);
    });

    summary.appendChild(chipList);

    if (filters.length > 1) {
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'b2-active-filter-summary__clear';
      clear.textContent = 'Rimuovi tutti';
      clear.addEventListener('click', clearFilters);
      summary.appendChild(clear);
    }

    existing?.remove();
    anchor.insertAdjacentElement('beforebegin', summary);
  };

  const localizeBoostUi = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    const lang = (document.documentElement.lang || '').toLowerCase();
    if (!root || !lang.startsWith('it')) return;

    root.querySelectorAll('.boost-sd__product-count').forEach((productCount) => {
      const raw = productCount.textContent || '';
      const text = raw.trim();
      const rangeMatch = text.match(/^Showing\s+(\d+)\s*-\s*(\d+)\s+of\s+(\d+)\s+products$/i);
      const totalMatch = text.match(/^(\d[\d.,]*)\s+products$/i);
      const turboTotalMatch = text.match(/^(?:Products?|Prodotti)\s+(\d[\d.,]*)$/i);

      let nextText = null;
      if (rangeMatch) {
        nextText = `Visualizzati ${rangeMatch[1]}–${rangeMatch[2]} di ${rangeMatch[3]} prodotti`;
      } else if (totalMatch) {
        nextText = `${totalMatch[1]} prodotti`;
      } else if (turboTotalMatch) {
        nextText = `${turboTotalMatch[1]} prodotti`;
      }

      if (nextText && productCount.textContent !== nextText) {
        productCount.textContent = nextText;
      }
    });

    if (window.location.pathname === '/search') {
      const searchHeading =
        document.querySelector('.boost-sd__header-title') ||
        Array.from(document.querySelectorAll('h1')).find((heading) =>
          /^Risultati della ricerca/i.test((heading.textContent || '').trim())
        );

      searchHeading?.classList.add('b2-search-results-heading');
    }

    const sortLabels = new Map([
      ['relevance', 'Rilevanza'],
      ['extra-sort1-descending', 'Ultimi arrivi'],
      ['price-ascending', 'Prezzo - dal più Basso'],
      ['price-descending', 'Prezzo - dal più Alto']
    ]);

    root.querySelectorAll('.boost-sd__sorting-option[data-value]').forEach((option) => {
      const label = sortLabels.get(option.getAttribute('data-value'));
      if (label && option.textContent.trim() !== label) {
        option.textContent = label;
      }
    });

    const activeSort = root.querySelector(
      '.boost-sd__sorting-option--active[data-value]'
    );
    const sortingValue = root.querySelector('.boost-sd__sorting-value');
    const activeLabel = activeSort
      ? sortLabels.get(activeSort.getAttribute('data-value'))
      : null;

    if (activeLabel && sortingValue && sortingValue.textContent.trim() !== activeLabel) {
      sortingValue.textContent = activeLabel;
    }

    const loadMore = Array.from(
      root.querySelectorAll('button, a, [role="button"]')
    ).find((element) => /^load more$/i.test((element.textContent || '').trim()));

    if (loadMore) loadMore.textContent = 'Vedi più prodotti';
  };

  const render = () => {
    renderFilterSummary();
    localizeBoostUi();
  };

  const schedule = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(render, 80);
  };

  /* COLLECTION-GENDER-NAV-01
     Boost's Collection filter is navigation, not a faceted refinement.
     Intercept only the gender-family values on dedicated SEO landing families
     and perform a normal browser navigation so Shopify renders the full
     authoritative document (H1, intro, title, meta description, canonical).
     Event-driven only: one delegated click listener, no polling/interval. */
  const COLLECTION_GENDER_FAMILIES = [
    {
      paths: new Set([
        '/collections/orologi',
        '/collections/orologi-uomo',
        '/collections/orologi-donna',
        '/collections/orologi-da-bambini'
      ]),
      routes: new Map([
        ['tutti', '/collections/orologi'],
        ['uomo', '/collections/orologi-uomo'],
        ['donna', '/collections/orologi-donna'],
        ['bambini', '/collections/orologi-da-bambini']
      ])
    },
    {
      paths: new Set([
        '/collections/gioielli',
        '/collections/gioielli-uomo',
        '/collections/gioielli-donna',
        '/collections/gioielli-da-bambini'
      ]),
      routes: new Map([
        ['tutti', '/collections/gioielli'],
        ['uomo', '/collections/gioielli-uomo'],
        ['donna', '/collections/gioielli-donna'],
        ['bambini', '/collections/gioielli-da-bambini']
      ])
    }
  ];

  const normalizeBoostValueLabel = (button) => {
    const labelNode = button.querySelector('.boost-sd__filter-option-item-label');
    const raw = (labelNode?.textContent || button.textContent || '').trim();

    return raw
      .replace(/\s*\(\s*\d[\d.,]*\s*\)\s*$/, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  };

  const navigateGenderCollection = (event) => {
    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (typeof event.button === 'number' && event.button > 0)
    ) return;

    const currentPath = window.location.pathname.replace(/\/$/, '');
    const family = COLLECTION_GENDER_FAMILIES.find((item) => item.paths.has(currentPath));
    if (!family) return;

    const origin = event.target;
    if (!(origin instanceof Element)) return;

    let destination = null;

    const button = origin.closest('.boost-sd__filter-option-item-button');
    if (button) {
      const label = normalizeBoostValueLabel(button);
      destination = family.routes.get(label) || null;
    }

    if (!destination) {
      const shortcut = origin.closest('.collection-shortcuts__link');
      if (shortcut) {
        try {
          const shortcutUrl = new URL(shortcut.href, window.location.origin);
          const shortcutPath = shortcutUrl.pathname.replace(/\/$/, '');
          if (
            shortcutUrl.origin === window.location.origin &&
            family.paths.has(shortcutPath)
          ) {
            destination = shortcutPath;
          }
        } catch (_) {}
      }
    }

    if (!destination) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (destination === currentPath) return;

    const targetUrl = buildCollectionDestination(destination);
    window.location.assign(targetUrl.toString());
  };

  if (!window.__b2GenderCollectionNavBound) {
    window.__b2GenderCollectionNavBound = true;
    window.addEventListener('click', navigateGenderCollection, true);
  }

  ['pushState', 'replaceState'].forEach((method) => {
    const original = history[method];
    if (typeof original !== 'function' || original.__b2CollectionWrapped) return;

    const wrapped = function (...args) {
      const manager = window.B2CollectionBrowseHistory;
      let callArgs = args;

      if (manager?.restoring) {
        const marker = manager.currentRestoreKey;
        if (marker) {
          const nextState = Object.assign({}, callArgs[0] || {});
          nextState.b2CollectionRestoreKey = marker;
          callArgs = [nextState, callArgs[1], callArgs[2]];
        }

        /* During automatic cumulative restoration, never add intermediate
           page=2/page=3 entries to browser history. Boost may advance its URL,
           but that progression belongs to the same restored collection entry. */
        if (method === 'pushState') {
          const result = history.replaceState.apply(history, callArgs);
          schedule();
          return result;
        }
      }

      const result = original.apply(this, callArgs);
      schedule();
      return result;
    };
    wrapped.__b2CollectionWrapped = true;
    history[method] = wrapped;
  });

  window.addEventListener('popstate', schedule);
  window.addEventListener('pageshow', schedule);
  window.addEventListener('resize', schedule);


  /* COLLECTION-HISTORY-01 v4
     Preserve cumulative Boost browsing context across PDP -> Browser Back.
     sessionStorage is the durable authority; history.state is advisory only.
     Lifecycle:
     DEPARTING_COLLECTION -> PRODUCT_SEEN -> RETURNING_COLLECTION -> RESTORED. */
  const browseHistory = window.B2CollectionBrowseHistory =
    window.B2CollectionBrowseHistory || {};

  const RESTORE_STATE_KEY = 'b2CollectionRestoreKey';
  const RESTORE_STORAGE_PREFIX = 'b2CollectionBrowse:v2:';
  const RESTORE_TTL = 30 * 60 * 1000;
  const PHASE_DEPARTING = 'DEPARTING_COLLECTION';
  const PHASE_PRODUCT_SEEN = 'PRODUCT_SEEN';
  const PHASE_RETURNING = 'RETURNING_COLLECTION';
  const PHASE_RESTORED = 'RESTORED';

  const currentLocationIsCollection = () =>
    window.location.pathname.startsWith('/collections/');

  const collectionUsesNumberedPagination = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return false;

    const hasLoadMore = Array.from(
      root.querySelectorAll('button, a, [role="button"]')
    ).some((element) => {
      const label = (element.textContent || '').trim();
      return (
        /^(Vedi più prodotti|Load more)$/i.test(label) &&
        !element.disabled &&
        element.getAttribute('aria-disabled') !== 'true' &&
        element.getClientRects().length > 0
      );
    });

    if (hasLoadMore) return false;

    return !!root.querySelector(
      '[class*="boost-sd__pagination"] a[href*="page="], [class*="boost-sd__pagination"] button[data-page]'
    );
  };

  const normalizedCollectionLocation = () => {
    const url = new URL(window.location.href);

    /* Shopify can append transient recommendation/session query keys to
       browser-history collection entries on Back in Firefox/WebKit. They are
       transport metadata, not part of the user's collection/filter state. */
    url.searchParams.delete('_su_rec');
    url.searchParams.delete('_su_rec_id');

    const page = Number(url.searchParams.get('page'));
    const preserveNumberedPage =
      Number.isInteger(page) &&
      page > 1 &&
      collectionUsesNumberedPagination();

    if (!preserveNumberedPage) {
      url.searchParams.delete('page');
    }

    url.hash = '';
    return url.pathname + (url.search ? url.search : '');
  };

  const browseStorageKey = (locationKey = normalizedCollectionLocation()) =>
    RESTORE_STORAGE_PREFIX + locationKey;

  const persistPayload = (key, payload) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(payload));
      return true;
    } catch (_) {
      return false;
    }
  };

  const countLoadedProducts = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return 0;

    const products = new Set();
    root.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        if (url.origin === window.location.origin && url.pathname.includes('/products/')) {
          products.add(url.pathname);
        }
      } catch (_) {}
    });
    return products.size;
  };

  const findLoadMoreControl = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return null;

    return Array.from(root.querySelectorAll('button, a, [role="button"]')).find((element) => {
      const label = (element.textContent || '').trim();
      if (!/^(Vedi più prodotti|Load more)$/i.test(label)) return false;
      if (element.disabled || element.getAttribute('aria-disabled') === 'true') return false;
      return element.getClientRects().length > 0;
    }) || null;
  };

  const navigationIsBackForward = () => {
    try {
      const entry = performance.getEntriesByType('navigation')[0];
      return entry && entry.type === 'back_forward';
    } catch (_) {
      return false;
    }
  };

  const readPendingPayload = () => {
    if (!currentLocationIsCollection()) return null;

    const locationKey = normalizedCollectionLocation();
    const fallbackKey = browseStorageKey(locationKey);
    const stateKey = (history.state || {})[RESTORE_STATE_KEY];
    const candidates = Array.from(new Set([fallbackKey, stateKey].filter(Boolean)));

    for (const key of candidates) {
      try {
        const payload = JSON.parse(window.sessionStorage.getItem(key) || 'null');
        if (!payload) continue;

        if (Date.now() - payload.savedAt > RESTORE_TTL) {
          window.sessionStorage.removeItem(key);
          continue;
        }

        if (
          payload.pendingReturn === true &&
          payload.locationKey === locationKey
        ) {
          return { key, payload };
        }
      } catch (_) {}
    }

    return null;
  };

  const productCardForLink = (root, link) =>
    link.closest('.boost-sd__product-item') ||
    link.closest('[class*="boost-sd__product-item"]') ||
    null;

  const productPathForCard = (card) => {
    const link = card && card.querySelector('a[href*="/products/"]');
    if (!link) return '';
    try {
      return new URL(link.href, window.location.origin).pathname;
    } catch (_) {
      return '';
    }
  };

  const findTargetCard = (payload) => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return null;

    const cards = Array.from(root.querySelectorAll('.boost-sd__product-item'));

    if (payload.productPath) {
      const exact = cards.find((card) => productPathForCard(card) === payload.productPath);
      if (exact) return exact;
    }

    if (
      Number.isInteger(payload.targetIndex) &&
      payload.targetIndex >= 0 &&
      payload.targetIndex < cards.length
    ) {
      return cards[payload.targetIndex];
    }

    return null;
  };

  const saveBrowseStateBeforeProduct = (link) => {
    if (!currentLocationIsCollection()) return;

    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    const locationKey = normalizedCollectionLocation();
    const key = browseStorageKey(locationKey);
    const loadedCount = countLoadedProducts();

    if (!loadedCount) return;

    let productPath = '';
    try {
      const productUrl = new URL(link.href, window.location.origin);
      if (
        productUrl.origin !== window.location.origin ||
        !productUrl.pathname.includes('/products/')
      ) return;
      productPath = productUrl.pathname;
    } catch (_) {
      return;
    }

    const targetCard = productCardForLink(root, link);
    const cards = Array.from(root.querySelectorAll('.boost-sd__product-item'));
    const targetIndex = targetCard ? cards.indexOf(targetCard) : -1;
    const targetRect = targetCard ? targetCard.getBoundingClientRect() : null;

    const payload = {
      version: 4,
      phase: PHASE_DEPARTING,
      locationKey,
      loadedCount,
      scrollY: window.scrollY || 0,
      productPath,
      targetIndex,
      targetViewportTop: targetRect ? targetRect.top : null,
      pendingReturn: true,
      savedAt: Date.now()
    };

    if (!persistPayload(key, payload)) return;

    /* Advisory only. TURBO may replace history.state; sessionStorage remains
       the durable authority for the pending return lifecycle. */
    const nextState = Object.assign({}, history.state || {});
    nextState[RESTORE_STATE_KEY] = key;

    history.replaceState(
      nextState,
      '',
      locationKey
    );

    browseHistory.pendingKey = key;
  };

  const waitForProductCountIncrease = (previousCount, timeout = 8000) =>
    new Promise((resolve) => {
      const startedAt = Date.now();

      const check = () => {
        const currentCount = countLoadedProducts();
        if (currentCount > previousCount || Date.now() - startedAt >= timeout) {
          resolve(currentCount);
          return;
        }
        window.setTimeout(check, 120);
      };

      check();
    });

  const waitForCollectionRoot = (timeout = 8000) =>
    new Promise((resolve) => {
      const startedAt = Date.now();

      const check = () => {
        const root = document.querySelector(ROOT_SELECTOR);
        if (root || Date.now() - startedAt >= timeout) {
          resolve(root || null);
          return;
        }
        window.setTimeout(check, 100);
      };

      check();
    });

  const waitForTargetCard = (payload, timeout = 8000) =>
    new Promise((resolve) => {
      const startedAt = Date.now();

      const check = () => {
        const target = findTargetCard(payload);
        if (target || Date.now() - startedAt >= timeout) {
          resolve(target || null);
          return;
        }
        window.setTimeout(check, 100);
      };

      check();
    });

  const settleFrames = (count = 2) =>
    new Promise((resolve) => {
      const step = (remaining) => {
        if (remaining <= 0) {
          resolve();
          return;
        }
        window.requestAnimationFrame(() => step(remaining - 1));
      };
      step(count);
    });

  const applyAndValidateSavedPosition = async (payload) => {
    if (Number.isFinite(payload.scrollY) && payload.scrollY >= 0) {
      window.scrollTo({ top: payload.scrollY, left: 0, behavior: 'auto' });
    }

    await settleFrames(2);

    let target = findTargetCard(payload);

    if (target && Number.isFinite(payload.targetViewportTop)) {
      let delta = target.getBoundingClientRect().top - payload.targetViewportTop;

      if (Math.abs(delta) > 1) {
        window.scrollTo({
          top: Math.max(0, window.scrollY + delta),
          left: 0,
          behavior: 'auto'
        });
        await settleFrames(2);
        target = findTargetCard(payload);
        delta = target
          ? target.getBoundingClientRect().top - payload.targetViewportTop
          : delta;
      }

      if (target && Math.abs(delta) > 20) {
        window.scrollTo({
          top: Math.max(0, window.scrollY + delta),
          left: 0,
          behavior: 'auto'
        });
        await settleFrames(2);
        target = findTargetCard(payload);
        delta = target
          ? target.getBoundingClientRect().top - payload.targetViewportTop
          : delta;
      }

      return !!target && Math.abs(delta) <= 20;
    }

    if (Number.isFinite(payload.scrollY) && payload.scrollY >= 0) {
      return Math.abs(window.scrollY - payload.scrollY) <= 20;
    }

    return false;
  };

  const cleanupNormalCollectionVisit = (key) => {
    try { window.sessionStorage.removeItem(key); } catch (_) {}
    browseHistory.pendingKey = null;
  };

  const restoreBrowseState = async (returnSignal = false, finalBoundary = false) => {
    if (!currentLocationIsCollection() || browseHistory.restoring) return;

    const pending = readPendingPayload();
    if (!pending) return;

    const { key, payload } = pending;

    if (!payload.phase) {
      payload.phase = PHASE_DEPARTING;
      persistPayload(key, payload);
    }

    const genuineReturn =
      returnSignal ||
      browseHistory.backSignal === true ||
      navigationIsBackForward();

    if (payload.phase === PHASE_DEPARTING) {
      return;
    }

    if (payload.phase === PHASE_PRODUCT_SEEN) {
      if (!genuineReturn) {
        /* A normal PDP -> Collection navigation is not Browser Back.
           Cancel the pending restore so it cannot replay on a later visit. */
        cleanupNormalCollectionVisit(key);
        return;
      }

      payload.phase = PHASE_RETURNING;
      payload.returningAt = Date.now();
      payload.savedAt = Date.now();
      if (!persistPayload(key, payload)) return;
    }

    if (payload.phase === PHASE_RESTORED) {
      cleanupNormalCollectionVisit(key);
      return;
    }

    if (payload.phase !== PHASE_RETURNING) return;

    /* A return signal may arrive before the collection document/lifecycle is
       actually ready (notably mobile TURBO popstate/back_forward). Arm the
       durable RETURNING phase, but do not apply or consume the restore until
       pageshow or the equivalent completed InstantClick return boundary. */
    if (!finalBoundary) return;

    browseHistory.backSignal = false;
    browseHistory.restoring = true;
    browseHistory.currentRestoreKey = key;

    try {
      const root = await waitForCollectionRoot();
      if (!root) return;

      const currentUrl = new URL(window.location.href);
      const savedLocationUrl = new URL(payload.locationKey, window.location.origin);
      const hasTransientPage =
        currentUrl.searchParams.has('page') &&
        !savedLocationUrl.searchParams.has('page');
      let loadedCount = countLoadedProducts();

      /* A genuine return can land on Boost's transient page=N segment.
         Canonicalize once while preserving RETURNING_COLLECTION in sessionStorage.
         The canonical reload may continue without a new browser-Back signal. */
      if (
        hasTransientPage &&
        loadedCount < payload.loadedCount &&
        payload.forceRestore !== true
      ) {
        payload.forceRestore = true;
        payload.savedAt = Date.now();
        if (!persistPayload(key, payload)) return;

        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('page');

        window.location.replace(
          cleanUrl.pathname + (cleanUrl.search ? cleanUrl.search : '') + (cleanUrl.hash || '')
        );
        return;
      }

      let attempts = 0;
      const maxAttempts = Math.max(4, Math.ceil(payload.loadedCount / 24) + 3);

      while (loadedCount < payload.loadedCount && attempts < maxAttempts) {
        const control = findLoadMoreControl();

        if (!control) {
          await new Promise((resolve) => window.setTimeout(resolve, 300));
          loadedCount = countLoadedProducts();
          attempts += 1;
          continue;
        }

        const before = loadedCount;
        control.click();
        loadedCount = await waitForProductCountIncrease(before);
        attempts += 1;

        if (loadedCount <= before) break;
      }

      /* TURBO can finish hydrating the current batch while this restore loop is
         waiting for pagination controls. Never classify a stale local count as
         a cumulative-restore failure. */
      loadedCount = countLoadedProducts();

      if (loadedCount < payload.loadedCount) {
        payload.lastRestoreFailure = 'loaded-count';
        payload.savedAt = Date.now();
        persistPayload(key, payload);
        return;
      }

      const target = await waitForTargetCard(payload);
      if (!target) {
        payload.lastRestoreFailure = 'target-card';
        payload.savedAt = Date.now();
        persistPayload(key, payload);
        return;
      }

      const finalUrl = new URL(payload.locationKey, window.location.origin);

      const cleanState = Object.assign({}, history.state || {});
      delete cleanState[RESTORE_STATE_KEY];

      browseHistory.currentRestoreKey = null;
      history.replaceState(
        cleanState,
        '',
        finalUrl.pathname + (finalUrl.search ? finalUrl.search : '') + (finalUrl.hash || '')
      );

      const restored = await applyAndValidateSavedPosition(payload);

      if (!restored) {
        payload.lastRestoreFailure = 'position-validation';
        payload.savedAt = Date.now();
        persistPayload(key, payload);
        return;
      }

      payload.phase = PHASE_RESTORED;
      payload.restoredAt = Date.now();
      payload.forceRestore = false;
      delete payload.lastRestoreFailure;
      persistPayload(key, payload);

      try { window.sessionStorage.removeItem(key); } catch (_) {}
      browseHistory.pendingKey = null;
    } finally {
      browseHistory.currentRestoreKey = null;
      browseHistory.restoring = false;
    }
  };

  const scheduleRestore = (returnSignal = false, finalBoundary = false) => {
    window.requestAnimationFrame(() =>
      restoreBrowseState(returnSignal, finalBoundary)
    );
  };

  const captureProductNavigation = (event) => {
    if (!currentLocationIsCollection()) return;

    if (
      event.defaultPrevented ||
      event.button > 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) return;

    const target = event.target;
    const link = target && target.closest && target.closest('a[href*="/products/"]');
    if (!link || link.target === '_blank') return;

    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    saveBrowseStateBeforeProduct(link);
  };

  if (!browseHistory.productClickBoundV2) {
    browseHistory.productClickBoundV2 = true;
    window.addEventListener('click', captureProductNavigation, true);
  }

  window.addEventListener('pageshow', (event) => {
    scheduleRestore(event.persisted || navigationIsBackForward(), true);
  });

  window.addEventListener('popstate', () => {
    browseHistory.backSignal = true;
    scheduleRestore(true, false);
  });

  const supportsDocumentScrollEnd = 'onscrollend' in document;
  browseHistory.scrollEndSupportedV4 = supportsDocumentScrollEnd;

  /* Treat scrollend as a useful signal, not as a correctness boundary.
     Firefox can emit an intermediate scrollend before its Back navigation
     finishes the final browser-managed scroll. Use the same RAF stability
     boundary on every engine so the semantic restore only runs after motion
     has actually settled. */
  if (!browseHistory.scrollSettleBoundaryBoundV5) {
    browseHistory.scrollSettleBoundaryBoundV5 = true;
    browseHistory.scrollSettleFallbackBoundV4 = true;

    let settleRaf = 0;
    let settleStableFrames = 0;
    let settleLastScrollY = null;

    const resetSettle = () => {
      if (settleRaf) window.cancelAnimationFrame(settleRaf);
      settleRaf = 0;
      settleStableFrames = 0;
      settleLastScrollY = null;
    };

    const settleTick = () => {
      settleRaf = 0;

      const pending = readPendingPayload();
      if (
        !pending ||
        pending.payload.phase !== PHASE_RETURNING ||
        browseHistory.restoring
      ) {
        resetSettle();
        return;
      }

      const currentScrollY = window.scrollY || 0;
      if (
        settleLastScrollY !== null &&
        Math.abs(currentScrollY - settleLastScrollY) < 0.5
      ) {
        settleStableFrames += 1;
      } else {
        settleStableFrames = 0;
      }
      settleLastScrollY = currentScrollY;

      if (settleStableFrames >= 24) {
        resetSettle();
        scheduleRestore(true, true);
        return;
      }

      settleRaf = window.requestAnimationFrame(settleTick);
    };

    const armSettle = () => {
      const pending = readPendingPayload();
      if (
        !pending ||
        pending.payload.phase !== PHASE_RETURNING ||
        browseHistory.restoring ||
        settleRaf
      ) return;

      settleStableFrames = 0;
      settleLastScrollY = null;
      settleRaf = window.requestAnimationFrame(settleTick);
    };

    document.addEventListener('scroll', armSettle, { passive: true });
    window.addEventListener('pageshow', armSettle);
    window.addEventListener('popstate', armSettle);

    if (supportsDocumentScrollEnd) {
      browseHistory.scrollEndBoundV4 = true;
      document.addEventListener('scrollend', armSettle);
    }
  }

  if (
    window.InstantClick &&
    typeof window.InstantClick.on === 'function' &&
    !browseHistory.instantClickBoundV2
  ) {
    browseHistory.instantClickBoundV2 = true;
    window.InstantClick.on('change', () => {
      const isCompletedReturn =
        currentLocationIsCollection() &&
        browseHistory.backSignal === true;
      scheduleRestore(isCompletedReturn, isCompletedReturn);
    });
  }

  const start = () => {
    const observer = new MutationObserver(schedule);
    const observerRoot = document.querySelector(ROOT_SELECTOR) || document.body;
    observer.observe(observerRoot, { childList: true, subtree: true });
    render();
    window.setTimeout(render, 400);
    window.setTimeout(render, 1200);
    scheduleRestore(navigationIsBackForward(), false);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
